// SPDX-FileCopyrightText: 2025 Slant Tech LLC
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import html2canvas from 'html2canvas';
import api from '../api';
import CommentMarker from './CommentMarker';
import DiffOverlay from './DiffOverlay';

/**
 * KiCanvasViewer - Displays KiCad schematics/PCBs with comment and diff overlays
 * 
 * This component uses the forked KiCanvas which exposes:
 * - kicanvas:viewportchange event - fires on pan/zoom with camera state
 * - kicanvas:click event - fires on click with world coordinates
 * - screenToWorld(x, y) method - convert screen to KiCad coordinates
 * - worldToScreen(x, y) method - convert KiCad to screen coordinates
 * - getViewportTransform() method - get current camera state
 * 
 * Comment markers are positioned using KiCad coordinates (mm) and transformed
 * to screen coordinates using the worldToScreen API, ensuring they stay
 * accurately positioned during pan/zoom operations.
 * 
 * Diff overlay shows added (green), removed (red), and modified (yellow)
 * elements when diff view is enabled.
 */
const KiCanvasViewer = forwardRef(function KiCanvasViewer({
  file,
  comments,
  selectedComment,
  isAddingComment,
  newCommentPosition,
  onCanvasClick,
  onCommentClick,
  projectId,
  mrIid,
  // Diff view props
  diffEnabled = false,
  diffData = null,
}, ref) {
  const containerRef = useRef(null);
  const kicanvasRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  
  // File bounds in KiCad mm coordinates (from backend)
  const [fileBounds, setFileBounds] = useState(null);
  const [components, setComponents] = useState([]);
  
  // Viewport transform state - updated via kicanvas:viewportchange event
  const [viewportTransform, setViewportTransform] = useState({
    centerX: 0,
    centerY: 0,
    zoom: 1,
    viewportWidth: 0,
    viewportHeight: 0,
  });

  // Track if KiCanvas API is available
  const [hasKiCanvasAPI, setHasKiCanvasAPI] = useState(false);

  // Use refs to avoid stale closures in event handlers
  const isAddingCommentRef = useRef(isAddingComment);
  const onCanvasClickRef = useRef(onCanvasClick);
  const findNearestComponentRef = useRef(null);

  // Keep refs up to date
  useEffect(() => {
    isAddingCommentRef.current = isAddingComment;
  }, [isAddingComment]);

  useEffect(() => {
    onCanvasClickRef.current = onCanvasClick;
  }, [onCanvasClick]);

  // Get file URL with project/MR context
  const filePath = file ? (file.path || file.new_path) : null;
  const fileUrl = filePath ? api.getFileUrl(filePath, null, mrIid, projectId) : null;

  /**
   * Convert screen coordinates (pixels) to KiCad coordinates (mm).
   * Uses the native KiCanvas API if available, otherwise falls back to
   * a calculation based on file bounds.
   */
  const screenToKicad = useCallback((screenX, screenY) => {
    const kicanvas = kicanvasRef.current;
    
    // Try native API first
    if (kicanvas && typeof kicanvas.screenToWorld === 'function') {
      const result = kicanvas.screenToWorld(screenX, screenY);
      if (result) {
        return { x: result.x, y: result.y };
      }
    }
    
    // Fallback: use file bounds to estimate (less accurate during pan/zoom)
    if (!containerSize.width || !containerSize.height || !fileBounds) {
      return null;
    }

    const boundsWidth = fileBounds.max_x - fileBounds.min_x;
    const boundsHeight = fileBounds.max_y - fileBounds.min_y;

    const kicadX = fileBounds.min_x + (screenX / containerSize.width) * boundsWidth;
    const kicadY = fileBounds.min_y + (screenY / containerSize.height) * boundsHeight;

    return { x: kicadX, y: kicadY };
  }, [containerSize, fileBounds]);

  /**
   * Convert KiCad coordinates (mm) to screen coordinates (pixels).
   * Uses the native KiCanvas API if available.
   */
  const kicadToScreen = useCallback((kicadX, kicadY) => {
    const kicanvas = kicanvasRef.current;
    
    // Try native API first - this is the key fix for marker positioning!
    if (kicanvas && typeof kicanvas.worldToScreen === 'function') {
      const result = kicanvas.worldToScreen(kicadX, kicadY);
      if (result) {
        return { x: result.x, y: result.y };
      }
    }
    
    // Fallback: use file bounds to estimate (less accurate during pan/zoom)
    if (!containerSize.width || !containerSize.height || !fileBounds) {
      return null;
    }

    const boundsWidth = fileBounds.max_x - fileBounds.min_x;
    const boundsHeight = fileBounds.max_y - fileBounds.min_y;

    const screenX = ((kicadX - fileBounds.min_x) / boundsWidth) * containerSize.width;
    const screenY = ((kicadY - fileBounds.min_y) / boundsHeight) * containerSize.height;

    return { x: screenX, y: screenY };
  }, [containerSize, fileBounds]);

  /**
   * Find the nearest component to a click position.
   */
  const findNearestComponent = useCallback((kicadX, kicadY, maxDistance = 15) => {
    if (!components.length) return null;

    let nearest = null;
    let minDist = maxDistance;

    for (const comp of components) {
      const dist = Math.sqrt(
        Math.pow(comp.x - kicadX, 2) + Math.pow(comp.y - kicadY, 2)
      );
      if (dist < minDist) {
        minDist = dist;
        nearest = comp;
      }
    }

    return nearest;
  }, [components]);

  // Keep findNearestComponent ref up to date
  useEffect(() => {
    findNearestComponentRef.current = findNearestComponent;
  }, [findNearestComponent]);

  // Fetch file info (bounds and components) when file changes
  useEffect(() => {
    if (!filePath || !projectId || !mrIid) {
      setFileBounds(null);
      setComponents([]);
      return;
    }

    let cancelled = false;

    async function fetchFileInfo() {
      try {
        const info = await api.getFileInfo(filePath, mrIid, projectId);
        if (!cancelled) {
          setFileBounds(info.bounds);
          setComponents(info.components || []);
        }
      } catch (err) {
        console.error('Failed to fetch file info:', err);
        if (!cancelled) {
          // Use default bounds if fetch fails
          setFileBounds({ min_x: 0, min_y: 0, max_x: 297, max_y: 210 });
        }
      }
    }

    fetchFileInfo();
    return () => { cancelled = true; };
  }, [filePath, projectId, mrIid]);

  /**
   * Get the internal canvas from KiCanvas element.
   * KiCanvas renders to a canvas inside its shadow DOM.
   * This function attempts to access that canvas directly for screenshot capture.
   */
  const getKiCanvasInternalCanvas = useCallback(() => {
    const kicanvas = kicanvasRef.current;
    if (!kicanvas) return null;

    try {
      // Method 1: Look for canvas in the shadow DOM
      if (kicanvas.shadowRoot) {
        // KiCanvas typically has a canvas element for rendering
        const canvas = kicanvas.shadowRoot.querySelector('canvas');
        if (canvas) {
          return canvas;
        }
      }

      // Method 2: Look for canvas in nested shadow DOMs (kc-board-viewer, kc-schematic-viewer)
      if (kicanvas.shadowRoot) {
        const viewers = kicanvas.shadowRoot.querySelectorAll('kc-board-viewer, kc-schematic-viewer, kc-board, kc-schematic');
        for (const viewer of viewers) {
          if (viewer.shadowRoot) {
            const canvas = viewer.shadowRoot.querySelector('canvas');
            if (canvas) {
              return canvas;
            }
          }
        }
      }
    } catch (err) {
      console.warn('Could not access KiCanvas internal canvas:', err);
    }

    return null;
  }, []);

  /**
   * Capture a screenshot of the area around a specific position.
   * Tries to use the KiCanvas internal canvas directly for better compatibility,
   * especially with PCB files that use WebGL rendering.
   * Falls back to html2canvas if the internal canvas is not accessible.
   */
  const captureScreenshot = useCallback(async (position) => {
    if (!containerRef.current || !isLoaded) return null;

    try {
      const cropSize = 300;

      // Convert KiCad position to current screen position
      const screenPos = kicadToScreen(position.x, position.y);
      if (!screenPos) return null;

      const centerX = screenPos.x;
      const centerY = screenPos.y;

      let sourceCanvas = null;

      // Try to get the internal KiCanvas canvas first (works better for PCB/WebGL)
      const internalCanvas = getKiCanvasInternalCanvas();
      if (internalCanvas && internalCanvas.width > 0 && internalCanvas.height > 0) {
        console.log('Using KiCanvas internal canvas for screenshot');
        sourceCanvas = internalCanvas;
      } else {
        // Fall back to html2canvas (works for simpler schematic rendering)
        console.log('Falling back to html2canvas for screenshot');

        // Temporarily hide the comment markers overlay during capture
        const markersOverlay = containerRef.current.querySelector('.comment-markers-overlay');
        if (markersOverlay) {
          markersOverlay.style.visibility = 'hidden';
        }

        sourceCanvas = await html2canvas(containerRef.current, {
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#1a1a2e',
          scale: 1,
          logging: false,
        });

        // Restore markers overlay visibility
        if (markersOverlay) {
          markersOverlay.style.visibility = 'visible';
        }
      }

      if (!sourceCanvas) {
        console.error('No canvas available for screenshot');
        return null;
      }

      // Create cropped canvas
      const croppedCanvas = document.createElement('canvas');
      croppedCanvas.width = cropSize;
      croppedCanvas.height = cropSize;
      const ctx = croppedCanvas.getContext('2d');

      // Calculate source coordinates, adjusting for canvas size vs container size
      const scaleX = sourceCanvas.width / containerRef.current.offsetWidth;
      const scaleY = sourceCanvas.height / containerRef.current.offsetHeight;

      const scaledCenterX = centerX * scaleX;
      const scaledCenterY = centerY * scaleY;
      const scaledCropSize = cropSize * Math.max(scaleX, scaleY);

      const sourceX = Math.max(0, Math.min(sourceCanvas.width - scaledCropSize, scaledCenterX - scaledCropSize / 2));
      const sourceY = Math.max(0, Math.min(sourceCanvas.height - scaledCropSize, scaledCenterY - scaledCropSize / 2));

      ctx.fillStyle = '#1a1a2e';
      ctx.fillRect(0, 0, cropSize, cropSize);

      ctx.drawImage(
        sourceCanvas,
        sourceX, sourceY, scaledCropSize, scaledCropSize,
        0, 0, cropSize, cropSize
      );

      return croppedCanvas.toDataURL('image/png').split(',')[1];
    } catch (err) {
      console.error('Failed to capture screenshot:', err);
      return null;
    }
  }, [isLoaded, kicadToScreen, getKiCanvasInternalCanvas]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    captureScreenshot,
    screenToKicad,
    kicadToScreen,
    findNearestComponent,
    getKiCanvasInternalCanvas,
    fileBounds,
    hasKiCanvasAPI,
  }), [captureScreenshot, screenToKicad, kicadToScreen, findNearestComponent, getKiCanvasInternalCanvas, fileBounds, hasKiCanvasAPI]);

  // Update container size on resize
  useEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      const rect = containerRef.current.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
    };

    updateSize();

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  // Handle KiCanvas element setup
  useEffect(() => {
    if (!containerRef.current || !fileUrl) return;

    setIsLoaded(false);
    setError(null);
    setHasKiCanvasAPI(false);
    setViewportTransform({
      centerX: 0,
      centerY: 0,
      zoom: 1,
      viewportWidth: 0,
      viewportHeight: 0,
    });

    // Clear previous content
    const container = containerRef.current;
    const existingEmbed = container.querySelector('kicanvas-embed');
    if (existingEmbed) {
      container.removeChild(existingEmbed);
    }

    // Create new KiCanvas element
    const kicanvas = document.createElement('kicanvas-embed');
    kicanvas.setAttribute('src', fileUrl);
    kicanvas.setAttribute('controls', 'full');
    kicanvas.setAttribute('controlslist', 'nodownload nooverlay');
    kicanvas.style.width = '100%';
    kicanvas.style.height = '100%';

    kicanvasRef.current = kicanvas;

    console.log('KiCanvas: Loading file from URL:', fileUrl);
    
    let hasLoaded = false;

    // Listen for viewport changes from the forked KiCanvas
    const handleViewportChange = (e) => {
      const detail = e.detail;
      if (detail) {
        setViewportTransform({
          centerX: detail.centerX,
          centerY: detail.centerY,
          zoom: detail.zoom,
          viewportWidth: detail.viewportWidth,
          viewportHeight: detail.viewportHeight,
        });
      }
    };

    // Listen for clicks from the forked KiCanvas - provides world coordinates
    const handleKiCanvasClick = (e) => {
      // Use ref to get current value (avoids stale closure)
      if (!isAddingCommentRef.current) return;
      
      const detail = e.detail;
      if (detail) {
        console.log('KiCanvas click:', detail);
        
        // Find nearest component for auto-reference
        const nearestComp = findNearestComponentRef.current?.(detail.worldX, detail.worldY);
        
        // Call the parent handler with KiCad coordinates
        onCanvasClickRef.current?.(detail.worldX, detail.worldY, nearestComp?.ref || null);
      }
    };

    kicanvas.addEventListener('kicanvas:viewportchange', handleViewportChange);
    kicanvas.addEventListener('kicanvas:click', handleKiCanvasClick);

    kicanvas.addEventListener('kicanvas:load', () => {
      console.log('KiCanvas: File loaded successfully');
      hasLoaded = true;
      setIsLoaded(true);
      
      // Check if the forked API is available
      if (typeof kicanvas.screenToWorld === 'function' &&
          typeof kicanvas.worldToScreen === 'function' &&
          typeof kicanvas.getViewportTransform === 'function') {
        console.log('KiCanvas: Forked API detected - using native coordinate transforms');
        setHasKiCanvasAPI(true);
        
        // Get initial viewport state
        const transform = kicanvas.getViewportTransform();
        if (transform) {
          setViewportTransform(transform);
        }
      } else {
        console.warn('KiCanvas: Standard KiCanvas detected - using fallback coordinate transforms');
      }
    });

    kicanvas.addEventListener('kicanvas:error', (e) => {
      console.error('KiCanvas: Error loading file:', e.detail || e);
      setError(`Failed to load file: ${e.detail?.message || 'Unknown error'}`);
    });

    // Also listen for generic error events
    kicanvas.addEventListener('error', (e) => {
      console.error('KiCanvas: Generic error:', e);
      setError('Failed to load KiCanvas viewer');
    });

    // Fallback timeout in case events don't fire
    const loadTimeout = setTimeout(() => {
      if (!hasLoaded) {
        console.warn('KiCanvas: Load timeout - checking if element is functional');
        // Check if the element has rendered anything
        if (kicanvas.shadowRoot && kicanvas.shadowRoot.children.length > 0) {
          console.log('KiCanvas: Element appears functional, marking as loaded');
          setIsLoaded(true);
        } else {
          console.error('KiCanvas: Element did not render after timeout');
        }
      }
    }, 5000);

    container.appendChild(kicanvas);

    return () => {
      clearTimeout(loadTimeout);
      kicanvas.removeEventListener('kicanvas:viewportchange', handleViewportChange);
      kicanvas.removeEventListener('kicanvas:click', handleKiCanvasClick);
      if (container.contains(kicanvas)) {
        container.removeChild(kicanvas);
      }
    };
  }, [fileUrl]); // Intentionally exclude isAddingComment and other callbacks

  // Handle click on container for adding comments (fallback for non-forked KiCanvas)
  const handleContainerClick = useCallback((e) => {
    // If we have the forked API, kicanvas:click handles this
    if (hasKiCanvasAPI) return;
    
    if (!isAddingComment || !containerRef.current || !fileBounds) return;

    // Don't trigger if clicking on a control element
    if (e.target.closest('button') || e.target.closest('[role="button"]')) return;

    const rect = containerRef.current.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    // Convert to KiCad coordinates
    const kicadPos = screenToKicad(screenX, screenY);
    if (!kicadPos) return;

    // Find nearest component for auto-reference
    const nearestComp = findNearestComponent(kicadPos.x, kicadPos.y);

    onCanvasClick(kicadPos.x, kicadPos.y, nearestComp?.ref || null);
  }, [hasKiCanvasAPI, isAddingComment, fileBounds, screenToKicad, findNearestComponent, onCanvasClick]);

  // Convert comment KiCad coordinates to screen position for markers
  const getMarkerPosition = useCallback((comment) => {
    // Use position_x/position_y (backend field names)
    const posX = comment.position_x ?? comment.x ?? 0;
    const posY = comment.position_y ?? comment.y ?? 0;
    return kicadToScreen(posX, posY);
  }, [kicadToScreen]);

  // Get screen position for new comment marker
  const getNewCommentScreenPosition = useCallback(() => {
    if (!newCommentPosition) return null;
    return kicadToScreen(newCommentPosition.x, newCommentPosition.y);
  }, [newCommentPosition, kicadToScreen]);

  const fileName = file?.name || filePath?.split('/').pop() || 'file';
  const newCommentScreenPos = getNewCommentScreenPosition();

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full min-h-0"
      onClick={handleContainerClick}
      style={{ cursor: isAddingComment ? 'crosshair' : 'default' }}
    >
      {/* Monochrome filter for diff view - applied to KiCanvas content */}
      {diffEnabled && isLoaded && (
        <style>{`
          /* Apply grayscale filter to the KiCanvas element when diff view is enabled */
          kicanvas-embed {
            filter: grayscale(70%) brightness(0.9) contrast(0.95);
            transition: filter 0.3s ease;
          }
        `}</style>
      )}

      {/* Loading overlay */}
      {!isLoaded && !error && (
        <div className="absolute inset-0 flex items-center justify-center z-10" style={{ background: 'rgb(var(--st-bg-primary))' }}>
          <div className="flex flex-col items-center gap-4">
            <div className="spinner w-8 h-8" />
            <span className="text-st-muted font-body">Loading {fileName}...</span>
          </div>
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center z-10" style={{ background: 'rgb(var(--st-bg-primary))' }}>
          <div className="text-center text-st-error">
            <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="font-body">{error}</p>
          </div>
        </div>
      )}

      {/* KiCanvas embed will be inserted here by useEffect */}

      {/* Diff overlay - rendered below comment markers */}
      {isLoaded && fileBounds && (
        <DiffOverlay
          diffData={diffData}
          kicadToScreen={kicadToScreen}
          containerSize={containerSize}
          isVisible={diffEnabled}
        />
      )}

      {/* Comment markers overlay */}
      {isLoaded && fileBounds && (
        <div className="comment-markers-overlay absolute inset-0 pointer-events-none z-20">
          {comments.map((comment, index) => {
            const position = getMarkerPosition(comment);
            if (!position) return null;

            // Check if marker is within visible bounds
            const isVisible = position.x >= -50 && position.x <= containerSize.width + 50 &&
                             position.y >= -50 && position.y <= containerSize.height + 50;
            
            if (!isVisible) return null;

            return (
              <CommentMarker
                key={comment.id}
                comment={comment}
                position={position}
                index={index + 1}
                isSelected={selectedComment?.id === comment.id}
                onClick={onCommentClick}
              />
            );
          })}

          {/* New comment position marker */}
          {newCommentScreenPos && (
            <div 
              className="comment-marker bg-st-warning animate-pulse pointer-events-none"
              style={{
                left: newCommentScreenPos.x,
                top: newCommentScreenPos.y,
              }}
            >
              +
            </div>
          )}
        </div>
      )}

      {/* Adding comment mode indicator */}
      {isAddingComment && isLoaded && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30">
          <div className="px-4 py-2 text-sm font-display font-semibold" style={{ background: 'rgb(var(--st-bg-secondary))', color: 'rgb(var(--st-text-primary))', border: '2px solid rgb(var(--st-border))' }}>
            Click on the {file?.file_type === 'pcb' ? 'PCB layout' : 'schematic'} to place a comment
          </div>
        </div>
      )}

      {/* Diff view indicator */}
      {diffEnabled && isLoaded && (
        <div className="absolute top-4 left-4 z-30 pointer-events-none">
          <div className="px-3 py-1.5 text-xs font-mono flex items-center gap-2 opacity-70" style={{ background: 'rgb(var(--st-bg-secondary) / 0.9)', color: 'rgb(var(--st-text-secondary))' }}>
            <svg className="w-3 h-3 text-st-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span>Diff</span>
            <span className="w-2 h-2 bg-st-success"></span>
            <span className="w-2 h-2 bg-st-error"></span>
            <span className="w-2 h-2 bg-st-warning"></span>
          </div>
        </div>
      )}

      {/* Coordinate info and API status */}
      {fileBounds && isLoaded && (
        <div className="absolute bottom-4 right-4 z-30 pointer-events-none text-xs font-mono flex items-center gap-2 px-2 py-1 opacity-70" style={{ background: 'rgb(var(--st-bg-secondary) / 0.9)', color: 'rgb(var(--st-text-muted))' }}>
          <span>{Math.round(fileBounds.max_x - fileBounds.min_x)}×{Math.round(fileBounds.max_y - fileBounds.min_y)}mm</span>
          {viewportTransform.zoom !== 1 && (
            <span>· {Math.round(viewportTransform.zoom * 100)}%</span>
          )}
          {hasKiCanvasAPI && (
            <span className="text-st-success" title="Using forked KiCanvas with native coordinate API">●</span>
          )}
          {diffEnabled && (
            <span className="text-st-accent" title="Diff view enabled">◐</span>
          )}
        </div>
      )}
    </div>
  );
});

export default KiCanvasViewer;
